import { Link } from "@tanstack/react-router";
import { trackEvent } from "@/lib/gtag";

// @ts-expect-error vite-imagetools transforms query at build time
import ageBabyAvif from "@/assets/montessori-hands.jpg?format=avif&w=720&quality=76";
// @ts-expect-error vite-imagetools transforms query at build time
import ageBabyWebp from "@/assets/montessori-hands.jpg?format=webp&w=720&quality=80";
// @ts-expect-error vite-imagetools transforms query at build time
import ageToddlerAvif from "@/assets/lifestyle-tower.jpg?format=avif&w=720&quality=76";
// @ts-expect-error vite-imagetools transforms query at build time
import ageToddlerWebp from "@/assets/lifestyle-tower.jpg?format=webp&w=720&quality=80";
// @ts-expect-error vite-imagetools transforms query at build time
import agePlayAvif from "@/assets/lifestyle-iglu.jpg?format=avif&w=720&quality=76";
// @ts-expect-error vite-imagetools transforms query at build time
import agePlayWebp from "@/assets/lifestyle-iglu.jpg?format=webp&w=720&quality=80";
// @ts-expect-error vite-imagetools transforms query at build time
import catPiklerAvif from "@/assets/lifestyle-pikler.jpg?format=avif&w=720&quality=76";
// @ts-expect-error vite-imagetools transforms query at build time
import catPiklerWebp from "@/assets/lifestyle-pikler.jpg?format=webp&w=720&quality=80";
// @ts-expect-error vite-imagetools transforms query at build time
import catTowerAvif from "@/assets/product-balance.jpg?format=avif&w=720&quality=76";
// @ts-expect-error vite-imagetools transforms query at build time
import catTowerWebp from "@/assets/product-balance.jpg?format=webp&w=720&quality=80";
// @ts-expect-error vite-imagetools transforms query at build time
import catToysAvif from "@/assets/product-modular.jpg?format=avif&w=720&quality=76";
// @ts-expect-error vite-imagetools transforms query at build time
import catToysWebp from "@/assets/product-modular.jpg?format=webp&w=720&quality=80";
// @ts-expect-error vite-imagetools transforms query at build time
import catIgluAvif from "@/assets/product-ballpit.jpg?format=avif&w=720&quality=76";
// @ts-expect-error vite-imagetools transforms query at build time
import catIgluWebp from "@/assets/product-ballpit.jpg?format=webp&w=720&quality=80";

/**
 * Shared card media block. AVIF → WebP, fixed aspect ratio so nothing shifts
 * while the image decodes (CLS = 0), lazy below the fold.
 */
function CardImage({
  avif,
  webp,
  alt,
  ratio,
}: {
  avif: string;
  webp: string;
  alt: string;
  ratio: string;
}) {
  return (
    <div className={`${ratio} overflow-hidden bg-sand`}>
      <picture>
        <source type="image/avif" srcSet={avif} />
        <source type="image/webp" srcSet={webp} />
        <img
          src={webp}
          alt={alt}
          width={720}
          height={900}
          loading="lazy"
          decoding="async"
          className="h-full w-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.03] motion-reduce:transform-none motion-reduce:transition-none"
        />
      </picture>
    </div>
  );
}

const AGES = [
  {
    label: "0–1 vuotta",
    hint: "Vauva",
    desc: "Kosketuskorit, peili ja lattiatason leikki.",
    search: "age-0-1" as const,
    avif: ageBabyAvif as string,
    webp: ageBabyWebp as string,
    alt: "Vauvan kädet tutkivat puisia Montessori-leluja lattialla",
  },
  {
    label: "1–3 vuotta",
    hint: "Taapero",
    desc: "Pikler-kiipeilytelineet ja oppimistornit.",
    search: "age-1-3" as const,
    avif: ageToddlerAvif as string,
    webp: ageToddlerWebp as string,
    alt: "Taapero seisoo oppimistornissa keittiön työtason ääressä",
  },
  {
    label: "3–6 vuotta",
    hint: "Leikki-ikä",
    desc: "Kiipeily, rakentelu ja roolileikki.",
    search: "age-3-6" as const,
    avif: agePlayAvif as string,
    webp: agePlayWebp as string,
    alt: "Lapsi leikkii pehmeillä leikkipalikoilla vaaleassa olohuoneessa",
  },
];

/** "Minkä ikäiselle etsit?" — the primary discovery tool on the homepage. */
export function AgeCards() {
  return (
    <section
      aria-labelledby="age-cards-title"
      className="border-t border-border bg-background"
    >
      <div className="container-prose section-y">
        <div className="max-w-2xl">
          <p className="text-[11px] font-medium uppercase tracking-[0.24em] text-clay">
            Aloita tästä
          </p>
          <h2
            id="age-cards-title"
            className="mt-3 font-display text-3xl italic text-foreground md:text-5xl text-balance"
          >
            Minkä ikäiselle etsit?
          </h2>
          <p className="mt-4 text-base text-muted-foreground">
            Löydä lapsen kehitysvaiheeseen sopivat tuotteet helposti.
          </p>
        </div>

        <div className="mt-10 grid gap-5 sm:grid-cols-3 md:mt-12 md:gap-6">
          {AGES.map((a) => (
            <Link
              key={a.search}
              to="/kauppa"
              search={{ kategoria: a.search }}
              onClick={() =>
                trackEvent("select_promotion", {
                  creative_name: "home_age_card",
                  promotion_name: a.label,
                  location_id: "home_age",
                })
              }
              className="group flex flex-col overflow-hidden rounded-2xl border border-border bg-cream transition-all duration-300 hover:-translate-y-0.5 hover:border-foreground/40 hover:shadow-[0_18px_40px_-28px_rgb(41_43_40/0.4)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground/60 focus-visible:ring-offset-2 focus-visible:ring-offset-background motion-reduce:transform-none"
            >
              <CardImage avif={a.avif} webp={a.webp} alt={a.alt} ratio="aspect-[4/3]" />
              <div className="flex flex-1 flex-col p-5 md:p-6">
                <p className="text-[10px] font-medium uppercase tracking-[0.22em] text-clay">
                  {a.hint}
                </p>
                <h3 className="mt-2 font-display text-2xl italic leading-none text-foreground">
                  {a.label}
                </h3>
                <p className="mt-2 text-sm text-muted-foreground">{a.desc}</p>
                <span className="mt-4 inline-flex min-h-[24px] items-center gap-1.5 text-sm font-medium text-foreground">
                  Näytä tuotteet
                  <span aria-hidden className="transition-transform group-hover:translate-x-1">
                    →
                  </span>
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

const CATEGORIES = [
  {
    to: "/kiipeilyteline",
    title: "Pikler-kiipeilytelineet",
    desc: "Liikettä, rohkeutta ja itsenäistä tutkimista.",
    avif: catPiklerAvif as string,
    webp: catPiklerWebp as string,
    alt: "Puinen Pikler-kiipeilyteline vaaleassa lastenhuoneessa",
  },
  {
    to: "/oppimistorni",
    title: "Oppimistornit",
    desc: "Lapsi mukaan keittiön ja kodin arkeen turvallisesti.",
    avif: catTowerAvif as string,
    webp: catTowerWebp as string,
    alt: "Puinen oppimistorni lapselle keittiössä",
  },
  {
    to: "/montessori-lelut",
    title: "Montessori-lelut",
    desc: "Leikkiä, joka kehittää keskittymistä ja taitoja.",
    avif: catToysAvif as string,
    webp: catToysWebp as string,
    alt: "Puisia Montessori-leluja lasten hyllyllä",
  },
  {
    to: "/iglu",
    title: "IGLU Soft Play",
    desc: "Pehmeää liikettä, rakentelua ja mielikuvitusta.",
    avif: catIgluAvif as string,
    webp: catIgluWebp as string,
    alt: "Pehmeitä IGLU-leikkipalikoita olohuoneen lattialla",
  },
] as const;

/** "Löydä oikea tuote" — four editorial category cards. */
export function CategoryCards() {
  return (
    <section
      aria-labelledby="category-cards-title"
      className="border-t border-border bg-sand"
    >
      <div className="container-prose section-y">
        <div className="max-w-2xl">
          <p className="text-[11px] font-medium uppercase tracking-[0.24em] text-clay">
            Kategoriat
          </p>
          <h2
            id="category-cards-title"
            className="mt-3 font-display text-3xl italic text-foreground md:text-5xl text-balance"
          >
            Löydä oikea tuote
          </h2>
        </div>

        <div className="mt-10 grid grid-cols-2 gap-4 md:mt-12 md:gap-6 lg:grid-cols-4">
          {CATEGORIES.map((c) => (
            <Link
              key={c.to}
              to={c.to}
              onClick={() =>
                trackEvent("select_promotion", {
                  creative_name: "home_category_card",
                  promotion_name: c.title,
                  location_id: "home_category",
                })
              }
              className="group flex flex-col overflow-hidden rounded-2xl border border-border bg-background transition-all duration-300 hover:-translate-y-0.5 hover:border-foreground/40 hover:shadow-[0_18px_40px_-28px_rgb(41_43_40/0.4)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground/60 focus-visible:ring-offset-2 focus-visible:ring-offset-background motion-reduce:transform-none"
            >
              <CardImage avif={c.avif} webp={c.webp} alt={c.alt} ratio="aspect-[4/5]" />
              <div className="flex flex-1 flex-col p-4 md:p-5">
                <h3 className="text-[15px] font-medium leading-snug text-foreground">
                  {c.title}
                </h3>
                <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                  {c.desc}
                </p>
                <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-foreground">
                  Katso
                  <span aria-hidden className="transition-transform group-hover:translate-x-1">
                    →
                  </span>
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
